import { PublicCallback } from 'globals/types';
import { importAndInitializePubNub } from 'core_main/helpers/vendors';
import { getSessionFromStore } from 'core_main/store';
import { getSession } from 'core_main/api/sessions';
import * as Pubnub from 'pubnub';
import { fetchMessageAttachments } from 'core_main/api/messages';
import { createGetMessagesCallbackResponse } from './helpers';
import {
  GetAllMessagesResolve,
  GetMessagesListMessage,
  GetMessagesParameters,
} from './types';

export const getAllMessages = (
  channelName: string,
  pubnub: Pubnub,
  options,
): Promise<GetAllMessagesResolve> => {
  const pageSize = options?.pageSize || 100;
  const page = options?.page || 1;

  return new Promise((resolve) => {
    const fetchMessages = (
      currentPage = 0,
      timetoken?: Pubnub.HistoryParameters['start'],
    ) => {
      return pubnub
        .history({
          channel: channelName,
          start: timetoken || 0,
          count: pageSize || 100,
        })
        .then((response: Pubnub.HistoryResponse) => {
          const { messages } = response;
          const nextPage = currentPage + 1;
          const start = response.startTimeToken;

          if (nextPage === page || !messages?.length) {
            return resolve({
              messages,
              pages: {
                next: messages?.length === pageSize ? page + 1 : null,
                previous: page === 1 ? null : page - 1,
                current: page,
              },
            });
          }

          return fetchMessages(nextPage, start);
        })
        .catch(() => {
          console.error('Failed to get messages');
        });
    };

    fetchMessages();
  });
};

export const getMessages = (
  parameters: GetMessagesParameters,
  cb?: PublicCallback,
) => {
  let pubnub;

  const conversationId = parameters?.conversationId;
  const pageSize = parameters?.pageSize;
  const page = parameters?.page;

  return importAndInitializePubNub()
    .then((module) => {
      pubnub = module;
      return getSessionFromStore(conversationId);
    })
    .then((sessionFromStore) => {
      if (!sessionFromStore) return getSession(conversationId);

      return undefined;
    })
    .then(() => {
      return getSessionFromStore(conversationId);
    })
    .then((conversation) => {
      const pubNubChannels = conversation?.attributes?.pubnub?.data?.channels;

      if (!pubNubChannels)
        throw new Error('Conversation PubNub channels not found');

      const conversationChannel = pubNubChannels.find(
        (channel) => channel.type === 'conversation',
      );

      if (typeof pubnub === 'object' && conversationChannel) {
        return getAllMessages(conversationChannel.name, pubnub, {
          pageSize,
          page,
        });
      }

      return undefined;
    })

    .then((history) => {
      const messagePromises = history?.messages.map((val) => {
        return new Promise((resolve) => {
          const attachments = val?.entry?.data?.relationships?.attachments;

          if (attachments) {
            return fetchMessageAttachments(val.entry.data.id).then(
              (attachmentsResponses) => {
                resolve({ ...val, attachments: attachmentsResponses });
              },
            );
          }

          return resolve(val);
        });
      }) as Promise<GetMessagesListMessage>[];

      return Promise.all(messagePromises).then((val) => {
        return {
          messages: val,
          pages: history?.pages,
        };
      });
    })
    .then((history) => {
      const callbackResponse = createGetMessagesCallbackResponse(
        history?.messages,
        history?.pages,
        conversationId,
      );

      if (typeof cb === 'function') {
        cb(callbackResponse);
      }
    })
    .catch((error) => {
      if (typeof cb === 'function') cb(null, error);
      console.error('Failed to get messages', error);
    });
};
